import type { UscEcoMeasureValuesType, WeightedSpecsLabelType } from "../../shared-logic/utils/uscEcoUtils";
import {
    amendWeightedTitle,
    getUscEcoMeasureValues,
    UscEcoValueMeasureSystem,
} from "../../shared-logic/utils/uscEcoUtils";
import { DfFuelTypeId } from "../../shared-logic/utils/dfConstants";
import type { QuickSpecConfigType, UscEcoQuickSpecs, UscVehicleType } from "../../shared-logic/utils/uscUtils";
import type { MeasureType } from "../../shared-logic/types/UscCommonTypes";
import type { UscSettingsType } from "../redux/reducers/UscSettingsReducer";
import { SpecSection } from "./constants";

export type UscEcoDropdownConfigType = {
    measure: MeasureType | null;
    title: string;
    tooltipReference?: UscEcoValueMeasureSystem;
};

/**
 * This maps the spec values to an object
 * The key is the spec type and the value is the spec label.
 * This way we can access the label of a spec by its type
 */
export const mapSpecsValues = (
    productInfo: UscVehicleType["product"],
    specs: QuickSpecConfigType[],
    ecoMeasureValues: UscEcoMeasureValuesType,
    weightedLabels: WeightedSpecsLabelType,
): Record<UscEcoQuickSpecs, string> => {
    return specs.reduce((acc, spec) => {
        // ⚠️ Edge case
        // The title from the content fragment will be overwritten here when a spec is weighted
        spec = amendWeightedTitle(
            productInfo,
            spec as QuickSpecConfigType<UscEcoQuickSpecs>,
            ecoMeasureValues,
            weightedLabels,
        );

        return { ...acc, [spec.type]: spec.title };
    }, {}) as Record<UscEcoQuickSpecs, string>;
};

/**
 * Returns the config of how the eco measure values with associated labels and tooltips should be displayed in the SectionConsumption
 * As this contains more than just consumption values it was renamed to something more general like EcoDropdown
 *
 * The ecoValues were split into part1 and part2 so the euroClass could be rendered between part1 and part2
 */
export const getUscEcoDropdownConfig = (
    productInfo: UscVehicleType["product"],
    uscSettings: UscSettingsType,
    weightedLabels: WeightedSpecsLabelType,
): {
    ecoValuesPart1: UscEcoDropdownConfigType[];
    ecoValuesPart2: UscEcoDropdownConfigType[];
    nedcIsNotAvailable: boolean;
} => {
    const ecoMeasureValues = getUscEcoMeasureValues(
        productInfo.eco,
        productInfo.engine.plugin || productInfo.engine.hybrid,
    );
    const { co2NedcCombined, fcNedcCombined } = ecoMeasureValues;
    const { ecCombined, eaerCombined, eaerCity, perCombined, perCity } = ecoMeasureValues;
    const { specCategories } = uscSettings;

    // True  when co2NedcCombined and fcNedcCombined are not available
    // Only useful for petrol, diesel and hybrid vehicles
    const nedcIsNotAvailable =
        !co2NedcCombined &&
        !fcNedcCombined &&
        [DfFuelTypeId.Petrol, DfFuelTypeId.Diesel, DfFuelTypeId.Hybrid].includes(
            productInfo.engine.fuelType.code as DfFuelTypeId,
        );

    const specCategoryConsumption = specCategories.find(
        (specCategory) => specCategory.type === SpecSection.Consumption,
    );

    if (!specCategoryConsumption) return { nedcIsNotAvailable, ecoValuesPart1: [], ecoValuesPart2: [] };

    const specCategoryConsumptionSpecs = mapSpecsValues(
        productInfo,
        specCategoryConsumption.specs,
        ecoMeasureValues,
        weightedLabels,
    );

    return {
        nedcIsNotAvailable,
        ecoValuesPart1: getUscFuelConsumptionConfig(productInfo, ecoMeasureValues, uscSettings, weightedLabels).filter(
            (item) => !!item.title,
        ),
        ecoValuesPart2: [
            ...getUscCo2EmissionConfig(productInfo, ecoMeasureValues, uscSettings, weightedLabels),
            {
                title: specCategoryConsumptionSpecs.ecCombined,
                measure: ecCombined,
                tooltipReference: UscEcoValueMeasureSystem.Wltp,
            },
            {
                title: specCategoryConsumptionSpecs.eaerCombined,
                measure: eaerCombined,
                tooltipReference: UscEcoValueMeasureSystem.Wltp,
            },
            {
                title: specCategoryConsumptionSpecs.eaerCity,
                measure: eaerCity,
                tooltipReference: UscEcoValueMeasureSystem.Wltp,
            },
            {
                title: specCategoryConsumptionSpecs.perCombined,
                measure: perCombined,
                tooltipReference: UscEcoValueMeasureSystem.Wltp,
            },
            {
                title: specCategoryConsumptionSpecs.perCity,
                measure: perCity,
                tooltipReference: UscEcoValueMeasureSystem.Wltp,
            },
        ].filter((item) => !!item.title),
    };
};

export const getUscFuelConsumptionConfig = (
    productInfo: UscVehicleType["product"],
    ecoMeasureValues: UscEcoMeasureValuesType,
    uscSettings: UscSettingsType,
    weightedLabels: WeightedSpecsLabelType,
): UscEcoDropdownConfigType[] => {
    const { fcNedcCombined, fcWltpCombined } = ecoMeasureValues;
    const { specCategories } = uscSettings;

    const consumptionCat = specCategories.find((specCategory) => specCategory.type === SpecSection.Consumption);

    if (!consumptionCat) return [];

    const consumptionSpecs = mapSpecsValues(productInfo, consumptionCat.specs, ecoMeasureValues, weightedLabels);

    // If the setting autoDetermineCo2Standard is true the WLTP combined will be used
    // And if the WLTP combined is not available, the NEDC combined will be used instead
    if (uscSettings.autoDetermineCo2Standard) {
        const fcCombined = fcWltpCombined || fcNedcCombined || null;
        // Fuel consumption (FE) WLTP or NEDC Combined
        return [
            {
                title: consumptionSpecs.consumption,
                measure: fcCombined,
            },
        ];
    }

    return [
        {
            title: consumptionSpecs.fcWltpCombined,
            measure: fcWltpCombined,
            tooltipReference: UscEcoValueMeasureSystem.Wltp,
        },
        {
            title: consumptionSpecs.fcNedcCombined,
            measure: fcNedcCombined,
            tooltipReference: UscEcoValueMeasureSystem.Nedc,
        },
    ];
};

export const getUscCo2EmissionConfig = (
    productInfo: UscVehicleType["product"],
    ecoMeasureValues: UscEcoMeasureValuesType,
    uscSettings: UscSettingsType,
    weightedLabels: WeightedSpecsLabelType,
): UscEcoDropdownConfigType[] => {
    const { combinedCo2, co2NedcCombined, co2WltpCombined } = ecoMeasureValues;
    const { specCategories } = uscSettings;

    const consumptionCat = specCategories.find((specCategory) => specCategory.type === SpecSection.Consumption);

    if (!consumptionCat) return [];

    const consumptionSpecs = mapSpecsValues(productInfo, consumptionCat.specs, ecoMeasureValues, weightedLabels);

    // If the setting autoDetermineCo2Standard is true the WLTP combined will be used
    // And if the WLTP combined is not available, the NEDC combined will be used instead
    if (uscSettings.autoDetermineCo2Standard) {
        return [{ title: consumptionSpecs.combinedCo2, measure: combinedCo2 }];
    } else {
        return [
            {
                title: consumptionSpecs.co2WltpCombined,
                measure: co2WltpCombined,
                tooltipReference: UscEcoValueMeasureSystem.Wltp,
            },
            {
                title: consumptionSpecs.co2NedcCombined,
                measure: co2NedcCombined,
                tooltipReference: UscEcoValueMeasureSystem.Nedc,
            },
        ];
    }
};
